import AlertDialog from "#components/AlertDialog/index.js";
import ColorPickerInput from "#components/ColorPickerInput/index.js";
import MDButton from "#components/MDButton/index.js";
import SelectionGrid from "#components/SelectionGrid/index.js";
import Stepper from "#components/Stepper/index.js";
import { ArrowDropDown, ArrowRight, Delete, Edit } from "@mui/icons-material";
import {
  Box,
  Checkbox,
  Chip,
  Collapse,
  FormControl,
  FormControlLabel,
  Grid,
  Icon,
  IconButton,
  InputAdornment,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";
import Button from "@mui/material/Button";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import {
  DatePicker,
  DateTimePicker,
  LocalizationProvider,
  TimePicker,
} from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import DropdownSelect from "components/DropdownSelect";
import LoadingButton from "components/LoadingButton";
import Dialog from "components/NoScrollDialog";
import TranslatableTextField from "components/TranslatableTextField";
import { useLanguage } from "contexts/language.context";
import dayjs from "dayjs";
import { SortableListContainer } from "layouts/logsheets/components/SortableListContainer";
import {
  DataContainer,
  PropertyDetails,
} from "layouts/logsheets/entities/AllEntities/EntitiesDialog";
import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import entitiesService from "services/entities-service";
import entityCategoriesService from "services/entity-categories-service";
import propertiesService from "services/properties-service";
import sheetTemplateCategoriesService from "services/sheet-template-categories-service";
import usersService from "services/users-service";
import CollapsibleSection from "../components/CollapsibleSection";
import departmentsService from "services/departments-service";

export default function TemplatesDialog({
  isOpen,
  onSubmit,
  data,
  type,
  onClose,
}) {
  const { label, languageId } = useLanguage();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const isInitialized = useRef(false);
  const [initialTemplate, setInitialTemplate] = useState({});
  const [updates, setUpdates] = useState({});
  const [options, setOptions] = useState({
    entities: [],
    entityCategories: [],
    templateCategories: [],
    units: [],
  });
  const [activeStep, setActiveStep] = useState(0);

  const template = useMemo(
    () => ({ ...initialTemplate, ...updates }),
    [initialTemplate, updates],
  );

  const steps = useMemo(() => {
    const steps = [];
    steps.push({
      label: "Setup template info",
      description: "Choose entity items and properties",
      level: 1,
      state: label("complete-label"),
    });

    if (template.type == 1)
      steps.push({
        label: "Setup properties",
        description: "Choose properties",
        level: 2,
        state: label("current-label"),
      });
    else
      steps.push({
        label: "Setup actions",
        description: "Choose actions",
        level: 2,
        state: label("current-label"),
      });

    if (template.type == 1)
      steps.push({
        label: "Setup entity elements",
        description: "Choose entity elements",
        level: 3,
        state: label("not-started-label"),
      });

    steps.push({
      label: "Setup schedule",
      description: "Choose when this template must be filled",
      level: template.type == 1 ? 4 : 3,
      state: label("not-started-label"),
    });
    return steps;
  }, [label, template.type]);

  async function fetch() {
    const responses = await Promise.all([
      entitiesService.getEntities(),
      entityCategoriesService.getEntityCategories(),
      sheetTemplateCategoriesService.getTemplateCategories(),
      usersService.getUsers({ view: "minimal" }),
      propertiesService.getProperties(),
      departmentsService.getDepartments(),
    ]);

    setOptions((prevOptions) => {
      const nextOptions = { ...prevOptions };

      if (responses[0][0]) nextOptions.entities = [...responses[0][1]];
      if (responses[1][0]) nextOptions.entityCategories = [...responses[1][1]];
      if (responses[2][0]) {
        nextOptions.templateCategories = [...responses[2][1]];
      }
      if (responses[3][0]) nextOptions.users = [...responses[3][1]];
      if (responses[4][0]) nextOptions.properties = [...responses[4][1]];
      if (responses[5][0]) nextOptions.departments = [...responses[5][1]];

      return nextOptions;
    });
  }

  useEffect(() => {
    if (isOpen && !isInitialized.current) {
      if (data?.template) {
        setInitialTemplate({
          ...data.template,
          entityIds: data.template?.entities?.map((e) => e.id),
          properties: data.template?.sheetTemplateSheetProperties,
          entityElements: data.template?.sheetTemplateEntityElements,
          name: data?.template?.allLanguageName || { en: "" },
        });
      } else {
        setInitialTemplate({
          name: { en: "" },
          properties: [],
          routine: { frequency: "Manual" },
          type: data.type,
          entityIds: [],
          notifications: [],
        });
      }
      setOpen(true);
      setLoading(false);
      setActiveStep(0);
      isInitialized.current = true;
      fetch();
    }

    if (!isOpen) {
      isInitialized.current = false;
      setUpdates({});
    }
  }, [isOpen, data.template]);

  useEffect(() => {
    if (!options.entities?.length || !template?.entityIds?.length) return;

    const entityIds = template.entityIds;
    const selectedEntities = options.entities.filter((e) =>
      entityIds.includes(e.id),
    );
    if (!selectedEntities.length) return;

    const totalEntities = selectedEntities.length;

    const propertyMap = {};
    selectedEntities.forEach((entity) => {
      (entity.entitySheetProperties || [])
        .filter((v) => v.type == (template.type || 1))
        .forEach((prop) => {
          const propId =
            prop.type == 2 ? `actionable-${prop.id}` : prop.sheetProperty?.id;
          if (!propId) return;
          if (!propertyMap[propId]) {
            propertyMap[propId] = {
              id: `${Date.now() * Math.random()}`,
              sheetProperty: prop.sheetProperty,
              sheetPropertyId: prop.type == 2 ? null : prop.sheetProperty?.id,
              sheetRule: prop.sheetRule,
              sheetRuleId: prop.sheetRuleId,
              name: prop.name || prop.sheetProperty?.name,
              required: false,
              selected: true,
              type: prop.type,
              entityIds: new Set(),
            };
          }
          propertyMap[propId].entityIds.add(entity.id);
        });
    });

    const mergedProperties = Object.values(propertyMap).map((prop) => {
      const isCommon = prop.entityIds.size === totalEntities;

      const storedProp = (template.properties || []).find((sp) => {
        if (prop.type == 2) {
          return !sp.sheetPropertyId && sp.name === prop.name;
        }
        return sp.sheetPropertyId === prop.sheetPropertyId;
      });

      return {
        ...prop,
        ...(storedProp
          ? {
              required: storedProp.required,
              selected: storedProp.selected,
              sequence: storedProp.sequence,
              name: storedProp.name || prop.name,
            }
          : {}),
        common: isCommon,
        availableFor: prop.entityIds.size,
        totalEntities,
        selected: storedProp ? storedProp.selected : isCommon ? true : false,
        notDraggable: !isCommon,
      };
    });

    setUpdates((prev) => ({
      ...prev,
      selectedEntities,
      properties: mergedProperties,
    }));
  }, [
    options.entities,
    data?.template,
    type,
    template?.entityIds,
    template?.type,
  ]);

  const categoryMap = useMemo(() => {
    const map = {};
    options.entityCategories?.forEach((cat) => {
      map[cat.id] = cat.name;
    });
    return map;
  }, [options.entityCategories]);

  const dropdownOptions = useMemo(() => {
    return {
      entities:
        options.entities?.map((entity) => ({
          label: entity.name,
          category: categoryMap[entity.entityCategoryId] || "Uncategorized",
          ...entity,
        })) || [],
      types: [
        { id: 1, label: "Measurable" },
        {
          id: 2,
          label: "Actionable",
        },
      ],

      properties: options.properties?.map((property) => {
        return {
          id: property.id,
          label: property.name,
        };
      }),
      departments: options.departments?.map((department) => {
        return {
          id: department.id,
          label: department.description,
        };
      }),
      users: options.users
        ?.filter((v) =>
          template.accountId ? v.accountId == template.accountId : true,
        )
        .map((user) => {
          return {
            id: user.id,
            label: user.name,
            account: user?.account?.name,
          };
        }),
      templateCategories: options.templateCategories?.map(
        (templateCategory) => {
          return {
            id: templateCategory.id,
            label: templateCategory.name,
          };
        },
      ),
    };
  }, [options, categoryMap, template.accountId]);

  const handleClose = () => {
    setOpen(false);
    onClose();
  };

  return (
    <Dialog
      fullWidth
      maxWidth="lg"
      open={open}
      onClose={handleClose}
      PaperProps={{
        component: "form",
        onSubmit: async (event) => {
          event.preventDefault();

          if (activeStep < steps.length - 1 && type == "create") {
            setActiveStep(activeStep + 1);
            return;
          }

          setLoading(true);
          const { selectedEntities, ...cleanTemplate } = template;
          const result = await onSubmit(cleanTemplate);
          if (result?.success) {
            handleClose();
          }
          setLoading(false);
        },
      }}
    >
      <DialogTitle>
        <Typography variant="h5" sx={{ mb: 1 }}>
          {type === "edit" ? "Edit Template" : "Create Template"}{" "}
          {template.name?.[languageId]
            ? `"${template.name?.[languageId]}"`
            : ""}
        </Typography>
        <Stepper
          steps={steps}
          activeStep={activeStep}
          mode={type}
          changeActiveStep={(stepIndex) => setActiveStep(stepIndex)}
        />
      </DialogTitle>

      <DialogContent
        sx={{
          maxHeight: "80vh",
        }}
      >
        {activeStep === 0 ? (
          <Tab0
            template={template}
            setUpdates={setUpdates}
            dropdownOptions={dropdownOptions}
            options={options}
          />
        ) : null}

        {activeStep === 1 ? (
          <Tab1Properties
            template={template}
            setUpdates={setUpdates}
            dropdownOptions={dropdownOptions}
            options={options}
          />
        ) : null}

        {activeStep === 2 && template.type == 1 ? (
          <Tab2EntityElements
            template={template}
            setUpdates={setUpdates}
            dropdownOptions={dropdownOptions}
          />
        ) : null}

        {activeStep === (template.type == 1 ? 3 : 2) ? (
          <Tab3Schedule template={template} setUpdates={setUpdates} />
        ) : null}
      </DialogContent>

      <DialogActions>
        <Box
          sx={{
            width: "100%",
            display: "flex",
            flexDirection: "row",
            gap: 1,
            justifyContent: "end",
          }}
        >
          <Button
            onClick={() => {
              setOpen(false);
            }}
          >
            {label("cancel-button")}
          </Button>
          {activeStep !== 0 && (
            <Button
              onClick={() => {
                if (activeStep !== 0) {
                  setActiveStep(activeStep - 1);
                }
              }}
            >
              {label("back-button")}
            </Button>
          )}
          {type == "edit" && activeStep !== steps.length - 1 && (
            <Button
              onClick={() => {
                setActiveStep(activeStep + 1);
              }}
            >
              {label("next-button")}
            </Button>
          )}
          <LoadingButton
            loading={loading.create}
            type="submit"
            color="info"
            variant="gradient"
          >
            {type == "create"
              ? activeStep === steps.length - 1
                ? label("finish-button")
                : label("next-button")
              : label("save-button")}
          </LoadingButton>
        </Box>
      </DialogActions>
    </Dialog>
  );
}

const genericTypes = [
  { id: "number", label: "Number", icon: "numbers" },
  { id: "boolean", label: "Yes / No", icon: "toggle_on" },
  { id: "datetime", label: "Date / Time", icon: "calendar_month" },
  { id: "datetime-auto", label: "Auto date / time", icon: "auto_mode" },
  { id: "audio", label: "Audio", icon: "audiotrack" },
  { id: "image", label: "Picture", icon: "image" },
  { id: "dropdown", label: "Dropdown", icon: "arrow_drop_down_circle" },
  { id: "text", label: "Text", icon: "text_fields" },
];

function Tab0({ template, setUpdates, dropdownOptions, options }) {
  const { label } = useLanguage();

  console.log("TEMPLATE: ", template);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        pt: 1.5,
      }}
    >
      <Grid container spacing={2}>
        <CollapsibleSection label="General info">
          <Grid container spacing={2} item xs={12}>
            <Grid item xs={6}>
              <TranslatableTextField
                autoFocus
                required
                id="name"
                name="name"
                label={label("name-singular")}
                type="text"
                values={template.name}
                onChange={(e) => {
                  setUpdates((prev) => ({
                    ...prev,
                    name: e.target.value,
                  }));
                }}
                fullWidth
              />
            </Grid>
            <Grid item xs={6}>
              <DropdownSelect
                required
                label={"Category"}
                initialValue={template?.sheetTemplateCategoryId || ""}
                onChange={(value) => {
                  setUpdates((prev) => {
                    return {
                      ...prev,
                      sheetTemplateCategoryId: value?.id || null,
                    };
                  });
                }}
                items={dropdownOptions.templateCategories}
                fullWidth
              />
            </Grid>
            <Grid item xs={6}>
              <DropdownSelect
                fullWidth
                label="Assigned to (default)"
                required
                initialValue={template.userId}
                onChange={(b) => setUpdates((u) => ({ ...u, userId: b?.id }))}
                items={dropdownOptions.users}
                groupBy={(option) => option.account}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Reference code"
                required
                value={template.referenceCode}
                onChange={(e) =>
                  setUpdates((u) => ({ ...u, referenceCode: e.target.value }))
                }
              />
            </Grid>
          </Grid>
        </CollapsibleSection>

        <CollapsibleSection label="Scope">
          <Grid container spacing={2} item xs={12}>
            {template?.type == 1 ? (
              <SelectionGrid
                xs={6}
                level={["baseEntity", "entity"]}
                baseEntityId={template.baseEntityId}
                setBaseEntityId={(baseEntityId) => {
                  setUpdates((u) => ({ ...u, baseEntityId }));
                }}
                entityId={template.entityId}
                setEntityId={(entityId) => {
                  setUpdates((u) => ({
                    ...u,
                    entityId,

                    entityElements: options.entities
                      .find((entity) => entity.id == entityId)
                      ?.entityElements?.map((entityElement) => ({
                        ...entityElement,
                        entityElementId: entityElement.id,
                        selected: true,
                        readOnly: true,
                        id: `${Date.now() * Math.random()}`,
                      })),
                    properties: options.entities
                      .find((entity) => entity.id == entityId)
                      ?.entitySheetProperties?.map((sheetProperty) => ({
                        ...sheetProperty,
                        entitySheetPropertyId: sheetProperty.id,
                        selected: true,
                        readOnly: true,
                        id: `${Date.now() * Math.random()}`,
                      })),
                  }));
                }}
              />
            ) : null}
            <SelectionGrid
              level={["account", "branchGroup", "branch"]}
              accountId={template.accountId}
              setAccountId={(accountId) => {
                setUpdates((prev) => ({ ...prev, accountId }));
              }}
              branchGroupId={template.branchGroupId}
              setBranchGroupId={(branchGroupId) => {
                setUpdates((prev) => ({ ...prev, branchGroupId }));
              }}
              branchId={template.branchId}
              setBranchId={(branchId) => {
                setUpdates((prev) => ({ ...prev, branchId }));
              }}
              xs={4}
            />
          </Grid>
        </CollapsibleSection>

        <CollapsibleSection label="Versioning">
          <Grid container spacing={2} item xs={12}>
            {" "}
            <Grid item xs={3}>
              <TextField
                fullWidth
                label="Version"
                required
                value={template.version}
                onChange={(e) =>
                  setUpdates((u) => ({ ...u, version: e.target.value }))
                }
              />
            </Grid>
            <Grid item xs={3}>
              <TextField
                fullWidth
                label="Issued by"
                required
                value={template.issuedBy}
                onChange={(e) =>
                  setUpdates((u) => ({ ...u, issuedBy: e.target.value }))
                }
              />
            </Grid>
            <Grid item xs={3}>
              <TextField
                fullWidth
                label="Approved by"
                required
                value={template.approvedBy}
                onChange={(e) =>
                  setUpdates((u) => ({ ...u, approvedBy: e.target.value }))
                }
              />
            </Grid>
            <Grid item xs={3}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DateTimePicker
                  label="Last revision date"
                  value={dayjs(template.lastRevisionDate)}
                  onChange={(d) =>
                    setUpdates((u) => ({ ...u, lastRevisionDate: d.toDate() }))
                  }
                  slotProps={{
                    textField: {
                      fullWidth: true,
                    },
                  }}
                />
              </LocalizationProvider>
            </Grid>
          </Grid>
        </CollapsibleSection>

        <CollapsibleSection label="Formatting">
          <Grid container spacing={2} item xs={12}>
            {" "}
            <Grid item xs={3}>
              <TextField
                fullWidth
                label="Icon"
                required
                value={template.icon}
                onChange={(e) =>
                  setUpdates((u) => ({ ...u, icon: e.target.value }))
                }
              />
            </Grid>
            <Grid item xs={3}>
              <TextField
                fullWidth
                label="Font"
                required
                value={template.font}
                onChange={(e) =>
                  setUpdates((u) => ({ ...u, font: e.target.value }))
                }
              />
            </Grid>
            <Grid item xs={3}>
              <ColorPickerInput
                required
                margin="dense"
                id="backgroundColor"
                name="backgroundColor"
                label={"Background color"}
                type="color"
                value={template.backgroundColor}
                onChange={(e) => {
                  setUpdates((u) => ({
                    ...u,
                    backgroundColor: e.target.value,
                  }));
                }}
                setValue={(v) => {
                  setUpdates((u) => ({ ...u, backgroundColor: v }));
                }}
                fullWidth
              />
            </Grid>
            <Grid item xs={3}>
              <ColorPickerInput
                required
                margin="dense"
                id="foregroundColor"
                name="foregroundColor"
                label={"Foreground color"}
                type="color"
                value={template.foregroundColor}
                onChange={(e) => {
                  setUpdates((u) => ({
                    ...u,
                    foregroundColor: e.target.value,
                  }));
                }}
                setValue={(v) => {
                  setUpdates((u) => ({ ...u, foregroundColor: v }));
                }}
                fullWidth
              />
            </Grid>
          </Grid>
        </CollapsibleSection>
        <CollapsibleSection
          label={`Notifications (${template.notifications?.length || 0})`}
        >
          <Grid item xs={12} container spacing={1}>
            {template.notifications?.length ? (
              template.notifications?.map((notification) => {
                return (
                  <Grid item xs={12} container spacing={1}>
                    <Grid item xs>
                      <Grid item xs>
                        <DropdownSelect
                          fullWidth
                          label="Scope"
                          required
                          initialValue={notification.scopeId}
                          disableClearable
                          onChange={(b) =>
                            setUpdates((u) => ({
                              ...u,
                              notifications: template.notifications?.map((n) =>
                                n.id == notification.id
                                  ? { ...n, scopeId: b?.id }
                                  : n,
                              ),
                            }))
                          }
                          items={[
                            {
                              id: 1,
                              label: "User",
                            },
                            {
                              id: 2,
                              label: "Department",
                            },
                          ]}
                        />
                      </Grid>
                    </Grid>
                    <Grid item xs>
                      <DropdownSelect
                        fullWidth
                        label={
                          notification.scopeId == 1 ? "User" : "Department"
                        }
                        required
                        initialValue={
                          notification[
                            notification.scopeId == 1
                              ? "userId"
                              : "departmentId"
                          ]
                        }
                        onChange={(b) =>
                          setUpdates((u) => ({
                            ...u,
                            notifications: template.notifications?.map((n) =>
                              n.id == notification.id
                                ? {
                                    ...n,
                                    [notification.scopeId == 1
                                      ? "userId"
                                      : "departmentId"]: b?.id,
                                  }
                                : n,
                            ),
                          }))
                        }
                        items={
                          dropdownOptions[
                            notification.scopeId == 1 ? "users" : "departments"
                          ]
                        }
                        groupBy={
                          notification.scopeId == 1
                            ? (option) => option.account
                            : null
                        }
                      />
                    </Grid>

                    <Grid item xs>
                      <DropdownSelect
                        fullWidth
                        label="When to notify"
                        required
                        initialValue={notification.when}
                        onChange={(b) =>
                          setUpdates((u) => ({
                            ...u,
                            notifications: template.notifications?.map((n) =>
                              n.id == notification.id
                                ? { ...n, when: b?.id }
                                : n,
                            ),
                          }))
                        }
                        items={[
                          {
                            id: 1,
                            label: "On corrective actions",
                          },
                          {
                            id: 2,
                            label: "On late",
                          },
                        ]}
                      />
                    </Grid>
                    <Grid item>
                      <IconButton
                        onClick={() => {
                          setUpdates((u) => ({
                            ...u,
                            notifications: u.notifications?.filter(
                              (n) => n.id != notification.id,
                            ),
                          }));
                        }}
                      >
                        <Delete />
                      </IconButton>
                    </Grid>
                  </Grid>
                );
              })
            ) : (
              <Grid item xs={12}>
                <Typography sx={{ fontSize: "14px", ml: 1 }}>
                  There are no notifications setup for this template
                </Typography>
              </Grid>
            )}
            <Grid item xs={12}>
              <MDButton
                size="small"
                onClick={() => {
                  setUpdates((u) => ({
                    ...u,
                    notifications: [
                      ...(u.notifications || []),
                      {
                        id: `${Date.now() * Math.random()}`,
                        userId: null,
                        when: 1,
                        scopeId: 1,
                      },
                    ],
                  }));
                }}
              >
                Add notification
              </MDButton>
            </Grid>
          </Grid>
        </CollapsibleSection>
      </Grid>
    </Box>
  );
}

function Tab1Properties({ template, setUpdates, dropdownOptions, options }) {
  const { label } = useLanguage();

  const handlePropertiesChange = (newProperties) => {
    setUpdates((prev) => ({
      ...prev,
      properties: newProperties,
    }));
  };

  return (
    <Box
      sx={{
        pt: 1.5,
      }}
    >
      {/* <DataContainer
        properties={template.properties || []}
        onPropertiesChange={handlePropertiesChange}
        dropdownOptions={dropdownOptions}
        options={options}
        readOnly
      /> */}
      <PropertiesContainer
        options={options}
        properties={template.properties || []}
        onPropertiesChange={handlePropertiesChange}
        dropdownOptions={dropdownOptions}
        selectedEntities={template.selectedEntities || []}
        template={template}
      />
    </Box>
  );
}

function Tab2EntityElements({ template, setUpdates, dropdownOptions }) {
  const { label } = useLanguage();

  const handleEntityElementsChange = (newEntityElements) => {
    setUpdates((prev) => ({
      ...prev,
      entityElements: newEntityElements,
    }));
  };

  return (
    <Box
      sx={{
        pt: 1.5,
      }}
    >
      <EntityElementsContainer
        entityElements={template.entityElements}
        onEntityElementsChange={handleEntityElementsChange}
        dropdownOptions={dropdownOptions}
      />
    </Box>
  );
}

function Tab3Schedule({ template, setUpdates }) {
  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 3, pt: 1.5 }}>
        <Grid container spacing={1}>
          <Grid item xs={12}>
            <Typography variant="h6">Frequency</Typography>
          </Grid>
          <Grid
            item
            xs={12}
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                // backgroundColor: "divider",
                width: "100%",
                gap: 1,
                borderRadius: "8px",
              }}
            >
              {[
                "Manual",
                "One time",
                "Daily",
                "Weekly",
                "Monthly",
                "Yearly",
              ].map((period) => {
                return (
                  <Chip
                    color={
                      template?.routine?.frequency == period
                        ? "info"
                        : "default"
                    }
                    sx={
                      template?.routine?.frequency == period
                        ? {
                            color: "white.main",
                            flex: 1,
                            borderRadius: "8px",
                            fontSize: "15px",
                            minHeight: "36px",
                          }
                        : {
                            flex: 1,
                            borderRadius: "8px",
                            fontSize: "15px",
                            py: 1,
                            minHeight: "36px",
                          }
                    }
                    label={period}
                    clickable
                    onClick={() => {
                      setUpdates((prev) => ({
                        ...prev,
                        routine: { ...template.routine, frequency: period },
                      }));
                    }}
                  />
                );
              })}
            </Box>
            <Box sx={{ width: "100%", mb: 4 }}>
              {template?.routine?.frequency == "One time" ? (
                <Grid container spacing={1}>
                  <Grid item xs={6}>
                    <DatePicker
                      label="Date"
                      value={
                        template?.routine?.startDate
                          ? dayjs(template.routine.startDate)
                          : null
                      }
                      onChange={(newValue) => {
                        if (!newValue) return;

                        setUpdates((prev) => {
                          const currentStart = dayjs(
                            prev?.routine?.startDate ||
                              template?.routine?.startDate ||
                              dayjs(),
                          );

                          const updatedDate = currentStart
                            .year(newValue.year())
                            .month(newValue.month())
                            .date(newValue.date());

                          return {
                            ...prev,
                            routine: {
                              ...template?.routine,
                              startDate: updatedDate,
                            },
                          };
                        });
                      }}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                        },
                      }}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <TimePicker
                      label="Time"
                      value={
                        template?.routine?.startDate
                          ? dayjs(template.routine.startDate)
                          : null
                      }
                      onChange={(newValue) => {
                        if (!newValue) return;

                        setUpdates((prev) => {
                          const currentStart = dayjs(
                            prev?.routine?.startDate ||
                              template?.routine?.startDate ||
                              dayjs(),
                          );

                          const updatedTime = currentStart
                            .hour(newValue.hour())
                            .minute(newValue.minute())
                            .second(newValue.second());

                          return {
                            ...prev,
                            routine: {
                              ...template?.routine,
                              startDate: updatedTime,
                            },
                          };
                        });
                      }}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                        },
                      }}
                    />
                  </Grid>
                </Grid>
              ) : null}

              {template?.routine?.frequency == "Daily" ? (
                <Grid container spacing={1}>
                  <Grid item xs={4}>
                    <TextField
                      fullWidth
                      label="Repeat every"
                      type="number"
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <Typography
                              sx={{ fontSize: "14px", fontWeight: "400" }}
                            >
                              days
                            </Typography>
                          </InputAdornment>
                        ),
                      }}
                      value={template?.routine?.interval || ""}
                      onChange={(e) => {
                        setUpdates((prev) => ({
                          ...prev,
                          routine: {
                            ...template.routine,
                            interval: Number(e.target.value),
                          },
                        }));
                      }}
                    />
                  </Grid>
                  <Grid item xs={8}>
                    <Typography
                      sx={{ fontSize: "14px", mb: -0.25, mt: -0.25, pl: 1 }}
                    >
                      Skip days:
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        gap: 1,
                        pl: 1,
                      }}
                    >
                      {["MO", "TU", "WE", "TH", "FR", "SA", "SU"].map(
                        (day, index) => {
                          return (
                            <Chip
                              label={day}
                              size="small"
                              color={
                                template?.routine?.dayRules?.[index] == "1"
                                  ? "primary"
                                  : "default"
                              }
                              sx={
                                template?.routine?.dayRules?.[index] == "1"
                                  ? {
                                      color: "white.main",
                                      minWidth: "0px",
                                    }
                                  : { minWidth: "0px" }
                              }
                              clickable
                              onClick={() => {
                                setUpdates((prev) => {
                                  let dayRules =
                                    template.routine?.dayRules || "0000000";
                                  dayRules = dayRules
                                    .split("")
                                    .map((v, i) =>
                                      i == index ? (v == "1" ? "0" : "1") : v,
                                    )
                                    .join("");

                                  return {
                                    ...prev,
                                    routine: {
                                      ...template.routine,
                                      dayRules,
                                    },
                                  };
                                });
                              }}
                            />
                          );
                        },
                      )}
                    </Box>
                  </Grid>

                  <Grid item xs={12}>
                    <RadioGroup
                      value={
                        template?.routine?.occurrencePerUnit
                          ? `${template?.routine?.occurrencePerUnit}`
                          : ""
                      }
                      onChange={(e) => {
                        setUpdates((prev) => ({
                          ...prev,
                          routine: {
                            ...template.routine,
                            occurrencePerUnit: Number(e.target.value),
                          },
                        }));
                      }}
                    >
                      <FormControlLabel
                        value="1"
                        control={<Radio />}
                        label={
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "row",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Typography
                              variant="inherit"
                              sx={{ whiteSpace: "nowrap" }}
                            >
                              Once per day at
                            </Typography>
                            <TimePicker
                              disabled={
                                template?.routine?.occurrencePerUnit != 1
                              }
                              value={template?.routine?.startTime}
                              onChange={(e) => {
                                setUpdates((prev) => ({
                                  ...prev,
                                  routine: {
                                    ...template.routine,
                                    startTime: e,
                                  },
                                }));
                              }}
                              slotProps={{
                                textField: {
                                  fullWidth: true,
                                  variant: "standard",
                                  size: "small",
                                  required: true,
                                },
                              }}
                            />
                          </Box>
                        }
                      />{" "}
                      <FormControlLabel
                        value="-1"
                        control={<Radio />}
                        label={
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "row",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Typography
                              variant="inherit"
                              sx={{ whiteSpace: "nowrap" }}
                            >
                              Multiple times a day every
                            </Typography>
                            <TextField
                              fullWidth
                              type="number"
                              variant="standard"
                              size="small"
                              required
                              disabled={
                                template?.routine?.occurrencePerUnit != -1
                              }
                              value={template?.routine?.subInterval}
                              onChange={(e) => {
                                setUpdates((prev) => ({
                                  ...prev,
                                  routine: {
                                    ...template.routine,
                                    subInterval: Number(e.target.value),
                                  },
                                }));
                              }}
                              InputProps={{
                                endAdornment: (
                                  <InputAdornment position="end">
                                    <Typography
                                      sx={{
                                        fontSize: "14px",
                                        fontWeight: "400",
                                      }}
                                    >
                                      hours
                                    </Typography>
                                  </InputAdornment>
                                ),
                              }}
                              sx={{ width: "100px" }}
                            />
                            <Typography
                              variant="inherit"
                              sx={{ whiteSpace: "nowrap" }}
                            >
                              starting at
                            </Typography>
                            <TimePicker
                              disabled={
                                template?.routine?.occurrencePerUnit != -1
                              }
                              value={template?.routine?.startTime}
                              onChange={(e) => {
                                setUpdates((prev) => ({
                                  ...prev,
                                  routine: {
                                    ...template.routine,
                                    startTime: e,
                                  },
                                }));
                              }}
                              slotProps={{
                                textField: {
                                  fullWidth: true,
                                  variant: "standard",
                                  size: "small",
                                  sx: { width: "100px" },
                                  required: true,
                                },
                              }}
                            />
                            <Typography
                              variant="inherit"
                              sx={{ whiteSpace: "nowrap" }}
                            >
                              and ending at
                            </Typography>
                            <TimePicker
                              disabled={
                                template?.routine?.occurrencePerUnit != -1
                              }
                              value={template?.routine?.endTime}
                              onChange={(e) => {
                                setUpdates((prev) => ({
                                  ...prev,
                                  routine: {
                                    ...template.routine,
                                    endTime: e,
                                  },
                                }));
                              }}
                              slotProps={{
                                textField: {
                                  fullWidth: true,
                                  variant: "standard",
                                  size: "small",
                                  sx: { width: "100px" },
                                  required: true,
                                },
                              }}
                            />
                          </Box>
                        }
                      />{" "}
                    </RadioGroup>
                  </Grid>
                </Grid>
              ) : null}

              {template?.routine?.frequency == "Weekly" ? (
                <Grid container spacing={1}>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="Repeat every"
                      type="number"
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <Typography
                              sx={{ fontSize: "14px", fontWeight: "400" }}
                            >
                              weeks
                            </Typography>
                          </InputAdornment>
                        ),
                      }}
                      value={template?.routine?.interval || ""}
                      onChange={(e) => {
                        setUpdates((prev) => ({
                          ...prev,
                          routine: {
                            ...template.routine,
                            interval: Number(e.target.value),
                          },
                        }));
                      }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Typography
                      sx={{ fontSize: "14px", mb: -0.25, mt: -0.25, pl: 1 }}
                    >
                      On days:
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        gap: 1,
                        pl: 1,
                      }}
                    >
                      {["MO", "TU", "WE", "TH", "FR", "SA", "SU"].map(
                        (day, index) => {
                          return (
                            <Chip
                              label={day}
                              size="small"
                              color={
                                template.routine?.dayRules?.[index] == "1"
                                  ? "info"
                                  : "default"
                              }
                              sx={
                                template.routine?.dayRules?.[index] == "1"
                                  ? {
                                      color: "white.main",
                                      minWidth: "0px",
                                    }
                                  : { minWidth: "0px" }
                              }
                              clickable
                              onClick={() => {
                                setUpdates((prev) => {
                                  let dayRules =
                                    template.routine?.dayRules || "0000000";
                                  dayRules = dayRules
                                    .split("")
                                    .map((v, i) =>
                                      i == index ? (v == "1" ? "0" : "1") : v,
                                    )
                                    .join("");

                                  return {
                                    ...prev,
                                    routine: {
                                      ...template.routine,
                                      dayRules,
                                    },
                                  };
                                });
                              }}
                            />
                          );
                        },
                      )}
                    </Box>
                  </Grid>
                </Grid>
              ) : null}

              {template?.routine?.frequency == "Monthly" ? (
                <Grid container spacing={1}>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="Repeat every"
                      type="number"
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <Typography
                              sx={{ fontSize: "14px", fontWeight: "400" }}
                            >
                              months
                            </Typography>
                          </InputAdornment>
                        ),
                      }}
                      value={template?.routine?.interval || ""}
                      onChange={(e) => {
                        setUpdates((prev) => ({
                          ...prev,
                          routine: {
                            ...template.routine,
                            interval: Number(e.target.value),
                          },
                        }));
                      }}
                    />
                  </Grid>
                  <Grid item xs={6}></Grid>
                  <Grid item xs={6}>
                    <FormControl>
                      <RadioGroup
                        value={
                          template.routine?.day == 32
                            ? "last"
                            : !template.routine?.day
                              ? "none"
                              : "day"
                        }
                        onChange={(e) => {
                          if (e.target.value == "last") {
                            setUpdates((prev) => ({
                              ...prev,
                              routine: {
                                ...template.routine,
                                day: 32,
                              },
                            }));
                          } else if (e.target.value == "none") {
                            setUpdates((prev) => ({
                              ...prev,
                              routine: {
                                ...template.routine,
                                day: 0,
                              },
                            }));
                          } else {
                            setUpdates((prev) => ({
                              ...prev,
                              routine: {
                                ...template.routine,
                                day: 1,
                              },
                            }));
                          }
                        }}
                      >
                        <FormControlLabel
                          value="day"
                          control={<Radio />}
                          label={
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "row",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              On day{" "}
                              <TextField
                                variant="standard"
                                size="small"
                                type="number"
                                defaultValue={1}
                                sx={{
                                  width: "auto !important",
                                  minWidth: "0px !important",
                                }}
                                required
                                disabled={
                                  !template.routine?.day ||
                                  template.routine?.day == 32
                                }
                                value={
                                  !template.routine?.day ||
                                  template.routine?.day == 32
                                    ? ""
                                    : template?.routine?.day || ""
                                }
                                onChange={(e) => {
                                  setUpdates((prev) => ({
                                    ...prev,
                                    routine: {
                                      ...template.routine,
                                      day: Number(e.target.value),
                                    },
                                  }));
                                }}
                              />
                            </Box>
                          }
                        />
                        <FormControlLabel
                          value="last"
                          control={<Radio />}
                          label="On last day"
                        />
                        <FormControlLabel
                          value="none"
                          control={<Radio />}
                          label={
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "row",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              On the{" "}
                              <DropdownSelect
                                size="small"
                                defaultValue={1}
                                items={[
                                  { id: 1, label: "First" },
                                  { id: 2, label: "Second" },
                                  { id: 3, label: "Third" },
                                  { id: 4, label: "Fourth" },
                                  { id: 5, label: "Last" },
                                ]}
                                disableClearable
                                sx={{ minWidth: "100px" }}
                                inputProps={{
                                  variant: "standard",
                                  size: "small",
                                }}
                                required
                                disabled={!!template.routine?.day}
                                initialValue={
                                  template?.routine?.dayRules?.split?.(
                                    "-",
                                  )?.[0] || ""
                                }
                                onChange={(e) => {
                                  setUpdates((prev) => {
                                    let dayRules =
                                      template.routine.dayRules || "0-0";

                                    dayRules = dayRules
                                      .split("-")
                                      .map((v, i) => (i == 0 ? e.id : v))
                                      .join("-");

                                    return {
                                      ...prev,
                                      routine: {
                                        ...template.routine,
                                        dayRules,
                                      },
                                    };
                                  });
                                }}
                              />
                              &nbsp;
                              <DropdownSelect
                                size="small"
                                defaultValue={1}
                                items={[
                                  { id: 1, label: "Monday" },
                                  { id: 2, label: "Tuesday" },
                                  { id: 3, label: "Wednesday" },
                                  { id: 4, label: "Thursday" },
                                  { id: 5, label: "Friday" },
                                  { id: 6, label: "Saturday" },
                                  { id: 7, label: "Sunday" },
                                ]}
                                disableClearable
                                sx={{ minWidth: "100px" }}
                                inputProps={{
                                  variant: "standard",
                                  size: "small",
                                }}
                                required
                                disabled={!!template.routine?.day}
                                initialValue={
                                  Number(
                                    template?.routine?.dayRules?.split?.(
                                      "-",
                                    )?.[1],
                                  ) || ""
                                }
                                onChange={(e) => {
                                  setUpdates((prev) => {
                                    let dayRules =
                                      template.routine.dayRules || "0-0";

                                    if (!dayRules.includes("-"))
                                      dayRules += "-";

                                    dayRules = dayRules
                                      .split("-")
                                      .map((v, i) => (i == 1 ? e.id : v))
                                      .join("-");

                                    return {
                                      ...prev,
                                      routine: {
                                        ...template.routine,
                                        dayRules,
                                      },
                                    };
                                  });
                                }}
                              />
                            </Box>
                          }
                        />
                      </RadioGroup>
                    </FormControl>
                  </Grid>
                </Grid>
              ) : null}

              {template?.routine?.frequency == "Yearly" ? (
                <Grid container spacing={1}>
                  <Grid item xs={4}>
                    <TextField
                      fullWidth
                      label="Repeat every"
                      type="number"
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <Typography
                              sx={{ fontSize: "14px", fontWeight: "400" }}
                            >
                              years
                            </Typography>
                          </InputAdornment>
                        ),
                      }}
                      value={template?.routine?.interval || ""}
                      onChange={(e) => {
                        setUpdates((prev) => ({
                          ...prev,
                          routine: {
                            ...template.routine,
                            interval: Number(e.target.value),
                          },
                        }));
                      }}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <DropdownSelect
                      label="Month"
                      defaultValue={"1"}
                      items={[
                        { id: 1, label: "January" },
                        { id: 2, label: "February" },
                        { id: 3, label: "March" },
                        { id: 4, label: "April" },
                        { id: 5, label: "May" },
                        { id: 6, label: "June" },
                        { id: 7, label: "July" },
                        { id: 8, label: "August" },
                        { id: 9, label: "September" },
                        { id: 10, label: "October" },
                        { id: 11, label: "November" },
                        { id: 12, label: "December" },
                      ]}
                      initialValue={template?.routine?.month || ""}
                      onChange={(e) => {
                        setUpdates((prev) => ({
                          ...prev,
                          routine: {
                            ...template.routine,
                            month: Number(e.id),
                          },
                        }));
                      }}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      label="Day"
                      fullWidth
                      value={template?.routine?.day || ""}
                      onChange={(e) => {
                        setUpdates((prev) => ({
                          ...prev,
                          routine: {
                            ...template.routine,
                            day: Number(e.target.value),
                          },
                        }));
                      }}
                    />
                  </Grid>
                </Grid>
              ) : null}
            </Box>
          </Grid>

          {["Daily", "Weekly", "Monthly", "Yearly"].includes(
            template?.routine?.frequency,
          ) ? (
            <Grid item xs={12}>
              <Typography variant="h6">End condition</Typography>
              <FormControl>
                <RadioGroup
                  defaultValue="Never"
                  value={template?.routine?.endMode}
                  onChange={(e) => {
                    setUpdates((prev) => ({
                      ...prev,
                      routine: {
                        ...template.routine,
                        endMode: e.target.value,
                      },
                    }));
                  }}
                >
                  <FormControlLabel
                    value="Never"
                    control={<Radio />}
                    label="Never"
                  />
                  <FormControlLabel
                    value="After count"
                    control={<Radio />}
                    label={
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "row",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        After{" "}
                        <TextField
                          required
                          disabled={template?.routine?.endMode != "After count"}
                          variant="standard"
                          size="small"
                          type="number"
                          value={template?.routine?.endCount || ""}
                          onChange={(e) => {
                            setUpdates((prev) => ({
                              ...prev,
                              routine: {
                                ...template.routine,
                                endCount: Number(e.target.value),
                              },
                            }));
                          }}
                          defaultValue={1}
                          sx={{
                            width: "auto !important",
                            minWidth: "0px !important",
                          }}
                        />
                        &nbsp;occurrences
                      </Box>
                    }
                  />

                  <FormControlLabel
                    value="At date"
                    control={<Radio />}
                    label={
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "row",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        On date{" "}
                        <DatePicker
                          disabled={template?.routine?.endMode != "At date"}
                          value={template?.routine?.endDate}
                          onChange={(e) => {
                            setUpdates((prev) => ({
                              ...prev,
                              routine: {
                                ...template.routine,
                                endDate: e,
                              },
                            }));
                          }}
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              variant: "standard",
                              sx: { width: "130px" },
                              required: true,
                            },
                          }}
                        />
                      </Box>
                    }
                  />
                </RadioGroup>
              </FormControl>
            </Grid>
          ) : null}
        </Grid>
      </Box>
    </LocalizationProvider>
  );
}

export function EntityElementsContainer({
  entityElements,
  onEntityElementsChange,
  dropdownOptions,
}) {
  return (
    <SortableListContainer
      title="Entity Items"
      items={entityElements}
      onChange={onEntityElementsChange}
      emptyMessage="Select an entity to show its items."
      editable={{ name: true }}
    />
  );
}

export function PropertiesContainer({
  properties,
  onPropertiesChange,
  dropdownOptions,
  options,
  selectedEntities,
  template,
}) {
  const [open, setOpen] = useState({ manage: false });

  const labelPlural = template.type == 1 ? "properties" : "actions";
  const labelSingular = template.type == 1 ? "property" : "action";
  const LabelPlural = template.type == 1 ? "Properties" : "Actions";
  const LabelSingular = template.type == 1 ? "Property" : "Action";

  return (
    <>
      {open.manage ? (
        <PropertyDialog
          dropdownOptions={dropdownOptions}
          options={options}
          initialProperty={open.manage.initialProperty}
          onClose={() => {
            setOpen((o) => ({ ...o, manage: false }));
          }}
          mode={open?.manage?.mode}
          onSubmit={(newProperty) => {
            if (open.manage?.mode == "create") {
              newProperty.selected = true;
              onPropertiesChange([...properties, newProperty]);
            } else {
              onPropertiesChange(
                properties.map((p) =>
                  p.id == newProperty.id ? { ...p, ...newProperty } : p,
                ),
              );
            }
          }}
          type={template.type}
        />
      ) : null}
      {open.alert ? (
        <AlertDialog
          open={open.alert}
          setOpen={(value) => {
            setOpen((curr) => ({
              ...curr,
              alert: value,
            }));
          }}
          title={`Delete ${labelSingular}?`}
          content={"Are you sure?"}
          onOk={async () => {
            const propId = open.alert.property.id;
            onPropertiesChange(properties.filter((p) => p.id != propId));
          }}
        />
      ) : null}
      <SortableListContainer
        action={
          <MDButton
            size="small"
            variant="gradient"
            color="info"
            onClick={() => {
              setOpen({
                ...open,
                manage: {
                  initialProperty: {
                    type: template.type,
                    id: `${Date.now() * Math.random()}`,
                  },
                  mode: "create",
                },
              });
            }}
          >
            Add {labelSingular}
          </MDButton>
        }
        title={LabelPlural}
        items={properties.map((p) => ({ ...p, type: template.type }))}
        onChange={onPropertiesChange}
        emptyMessage={`No ${labelPlural} yet`}
        render={{
          itemExtra: (property, handleItemChange) => {
            const isCommon = property.common !== false;
            const entityNames = isCommon
              ? []
              : (selectedEntities || [])
                  .filter((e) => property.entityIds?.has(e.id))
                  .map((e) => e.name);

            return (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  width: "100%",
                  alignItems: "center",
                  pl: 1,
                  opacity: isCommon ? 1 : 0.5,
                }}
              >
                {property.type == 1 || !property.type ? (
                  <Chip
                    sx={{ ml: -2 }}
                    size="small"
                    label={
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "row",
                          alignItems: "center",
                          gap: 0.5,
                        }}
                      >
                        {
                          genericTypes.find(
                            (v) => v.id == property?.sheetProperty?.generic,
                          )?.label
                        }
                        <Icon>
                          {
                            genericTypes.find(
                              (v) => v.id == property?.sheetProperty?.generic,
                            )?.icon
                          }
                        </Icon>
                      </Box>
                    }
                  />
                ) : null}

                <Box
                  sx={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "end",
                    alignItems: "center",
                    gap: 2,
                  }}
                >
                  {property.entitySheetPropertyId ? null : (
                    <>
                      <IconButton
                        size="small"
                        onClick={() => {
                          setOpen((o) => ({
                            ...o,
                            manage: {
                              mode: "edit",
                              initialProperty: property,
                            },
                          }));
                        }}
                      >
                        <Edit />
                      </IconButton>
                      <IconButton
                        onClick={() => {
                          setOpen((o) => ({
                            ...o,
                            alert: {
                              property,
                            },
                          }));
                        }}
                      >
                        <Delete />
                      </IconButton>
                    </>
                  )}
                </Box>
              </Box>
            );
          },
        }}
        editable={{ name: true }}
      />
    </>
  );
}

function PropertyDialog({
  dropdownOptions,
  options,
  onSubmit,
  initialProperty,
  onClose,
  mode,
  type,
}) {
  const [updates, setUpdates] = useState({});
  return (
    <Dialog open={true} onClose={onClose}>
      <DialogTitle>
        {mode == "create" ? "Create" : "Edit"}{" "}
        {type == 1 ? "property" : "action"}
      </DialogTitle>
      <DialogContent>
        <PropertyDetails
          initialProperty={{ ...initialProperty, ...updates }}
          dropdownOptions={dropdownOptions}
          options={options}
          onSave={(changes) => {
            setUpdates((u) => ({ ...u, ...changes }));
          }}
        />
      </DialogContent>
      <DialogActions>
        <MDButton onClick={onClose}>Cancel</MDButton>
        <MDButton
          variant="gradient"
          color="info"
          onClick={() => {
            onSubmit({ ...initialProperty, ...updates });
            onClose();
          }}
        >
          Submit
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}
